import React, { useMemo } from 'react';
import { Cell, Label, Pie, PieChart, Tooltip } from 'recharts';

import {
  CHART_COLOR_VARS,
  COLOR_MUTED,
  colorForSeriesIndex,
} from '@/pages/analytics/utils/chartPalette';
import { foldTopN, OTHER_SLICE_KEY } from '@/pages/analytics/utils/series';

export interface DonutRow {
  label: string;
  callCount: number;
  /** Raw (unformatted) value; also the stable key of the slice. */
  value: string;
  /** Slice colour. When absent it is assigned by position. */
  color?: string;
}

interface DonutDatum extends DonutRow {
  color: string;
  displayLabel: string;
  share: string;
}

const DONUT_SIZE = 144;
const DONUT_INNER_RADIUS = 45;
const DONUT_OUTER_RADIUS = 64;

const formatValuePart = (value: string): string =>
  value
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());

export const formatValue = (value: string): string => {
  if (value === '—') return value;

  const formatted = value.split(',').map(formatValuePart).filter(Boolean).join(', ');

  return formatted || value;
};

export const formatShare = (count: number, total: number): string => {
  if (total <= 0) return '0%';

  const percentage = (count / total) * 100;
  if (percentage >= 10) return `${Math.round(percentage)}%`;
  if (percentage >= 1) return `${percentage.toFixed(1)}%`;
  return `${percentage.toFixed(2)}%`;
};

const DonutTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload?: DonutDatum }>;
  unit: string;
}> = ({ active, payload, unit }) => {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md">
      <p className="max-w-56 truncate font-medium leading-5 text-foreground">{row.displayLabel}</p>
      <div className="mt-0.5 flex items-center gap-3 leading-5">
        <span className="font-semibold text-foreground tabular-nums">
          {row.callCount} {unit}
        </span>
        <span className="text-muted-foreground tabular-nums">{row.share}</span>
      </div>
    </div>
  );
};

/**
 * Keeps the `topN` largest rows and sums the rest into one muted `Other` row (ties by label).
 * Kept rows retain their colour, so a program keeps its colour across charts.
 */
const foldRows = (rows: DonutRow[], topN: number): DonutRow[] => {
  const byKey = new Map(rows.map(r => [r.value, r]));
  return foldTopN(
    rows.map(r => ({ key: r.value, name: r.label, value: r.callCount })),
    topN,
  ).map(slice =>
    slice.key === OTHER_SLICE_KEY && !byKey.has(OTHER_SLICE_KEY)
      ? { label: slice.name, callCount: slice.value, value: slice.key, color: COLOR_MUTED }
      : { ...(byKey.get(slice.key) as DonutRow) },
  );
};

export const DonutBreakdown: React.FC<{
  rows: DonutRow[];
  totalCalls: number;
  title: string;
  /** When set, renders a centered label inside the donut hole (e.g. "13 / not scored"). */
  center?: { primary: string; secondary?: string };
  /** Skip formatValue on labels — pass true when rows already carry display-ready labels. */
  rawLabels?: boolean;
  /** Show only the largest `topN` slices and fold the rest into "Other". */
  topN?: number;
  /** What a slice counts, for the tooltip and the accessible name. Defaults to "calls". */
  unit?: string;
}> = ({ rows, totalCalls, title, center, rawLabels, topN, unit = 'calls' }) => {
  const data: DonutDatum[] = useMemo(() => {
    const shown = topN === undefined ? rows : foldRows(rows, topN);
    // More than 5 slices would repeat the 5 theme chart colours.
    const fallback = (index: number): string =>
      shown.length > CHART_COLOR_VARS.length
        ? colorForSeriesIndex(index)
        : CHART_COLOR_VARS[index % CHART_COLOR_VARS.length];
    return shown.map((row, index) => ({
      ...row,
      color: row.color ?? fallback(index),
      displayLabel: rawLabels ? row.label : row.label === '—' ? '— (none)' : formatValue(row.label),
      share: formatShare(row.callCount, totalCalls),
    }));
  }, [rows, topN, rawLabels, totalCalls]);

  return (
    <div className="relative mx-auto flex h-36 w-36 items-center justify-center">
      <div role="img" aria-label={`${title} donut chart, ${totalCalls} ${unit}`}>
        <PieChart width={DONUT_SIZE} height={DONUT_SIZE}>
          <Pie
            data={data}
            dataKey="callCount"
            nameKey="displayLabel"
            cx="50%"
            cy="50%"
            innerRadius={DONUT_INNER_RADIUS}
            outerRadius={DONUT_OUTER_RADIUS}
            stroke="hsl(var(--background))"
            strokeWidth={2}
            isAnimationActive={false}
          >
            {data.map(row => (
              <Cell key={`${row.value}|${row.label}`} fill={row.color} />
            ))}
            {center && (
              <Label
                position="center"
                content={({ viewBox }) => {
                  const { cx, cy } = (viewBox as { cx: number; cy: number }) ?? { cx: 0, cy: 0 };
                  return (
                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                      <tspan
                        x={cx}
                        dy="-0.35em"
                        style={{
                          fontSize: 20,
                          fontWeight: 600,
                          fill: 'hsl(var(--foreground))',
                        }}
                      >
                        {center.primary}
                      </tspan>
                      {center.secondary && (
                        <tspan
                          x={cx}
                          dy="1.5em"
                          style={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        >
                          {center.secondary}
                        </tspan>
                      )}
                    </text>
                  );
                }}
              />
            )}
          </Pie>
          <Tooltip
            content={({ active, payload }) => (
              <DonutTooltip
                active={active}
                payload={payload as Array<{ payload?: DonutDatum }> | undefined}
                unit={unit}
              />
            )}
            cursor={false}
            wrapperStyle={{ outline: 'none', zIndex: 20 }}
          />
        </PieChart>
      </div>
    </div>
  );
};
